import { MatButtonModule } from '@angular/material/button';
import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.css'],
})
export class ConfirmDialogComponent implements OnInit {
  title: string;
  message: string;
  cancelButton: string | null;
  confirmButton: string;
  confirmButtonColor: 'primary' | 'warn' = 'primary';
  messageBlocks: Array<
    | { type: 'p'; text: string }
    | { type: 'ul'; items: string[] }
  > = [];

  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogModel
  ) {
    // Update view with given values
    this.title = data?.title ?? '';
    this.message = data?.message ?? '';

    const looksLikeDelete =
      /löschen/i.test(this.title) || /delete/i.test(this.title) || /löschen/i.test(this.message);

    // Handle cancelButton: if explicitly null, hide button; if undefined, use default 'Abbrechen'; otherwise use provided value
    if (data?.cancelButton === null) {
      this.cancelButton = null; // Hide button
    } else if (data?.cancelButton === undefined) {
      this.cancelButton = 'Abbrechen'; // Default
    } else {
      const trimmed = data.cancelButton.trim();
      this.cancelButton = trimmed || null; // Use provided value or hide if empty
    }
    
    this.confirmButton = data?.confirmButton?.trim() || (looksLikeDelete ? 'Löschen' : 'OK');
    this.confirmButtonColor = looksLikeDelete ? 'warn' : 'primary';

    this.messageBlocks = this.buildMessageBlocks(this.message);
  }

  ngOnInit() {}

  onConfirm(): void {
    // Close the dialog, return true
    this.dialogRef.close(true);
  }

  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  private buildMessageBlocks(message: string): Array<
    | { type: 'p'; text: string }
    | { type: 'ul'; items: string[] }
  > {
    const normalized = (message ?? '').replace(/\r\n/g, '\n').trim();
    if (!normalized) return [];

    const blocks: Array<{ type: 'p'; text: string } | { type: 'ul'; items: string[] }> = [];
    const paragraphs = normalized.split(/\n{2,}/g);

    for (const paragraph of paragraphs) {
      const lines = paragraph
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

      const bulletItems: string[] = [];
      const textLines: string[] = [];

      for (const line of lines) {
        if (/^-\s+/.test(line)) {
          bulletItems.push(line.replace(/^-+\s+/, '').trim());
        } else {
          textLines.push(line);
        }
      }

      if (textLines.length) {
        blocks.push({ type: 'p', text: textLines.join(' ') });
      }
      if (bulletItems.length) {
        blocks.push({ type: 'ul', items: bulletItems });
      }
    }

    return blocks;
  }
}

/**
 * Class to represent confirm dialog model.
 *
 * It has been kept here to keep it as part of shared component.
 */
export class ConfirmDialogModel {
  constructor(
    public title: string,
    public message: string,
    public cancelButton: string,
    public confirmButton: string
  ) {}
}
